import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, publishReplay, refCount, switchAll, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { LayerDescriptor } from './data';
import { DateRange, InterpolationService, UTCDate } from 'map-wald';
import { FeatureDataService } from './feature-data.service';
import { CacheService } from './cache.service';
import * as R from 'ramda';

const DEFAULT_ATTRIBUTION_TEXT='ANU Centre for Water and Landscape Dynamics';
const DEFAULT_ATTRIBUTION_URL='https://missing-attribution.anu.edu.au/';
const DEFAULT_LAYER_SETTINGS={
  attribution:DEFAULT_ATTRIBUTION_TEXT,
  attributionURL:DEFAULT_ATTRIBUTION_URL,
  dataModes:true,
  scale:1.0,
  resolution:0.1
}

const DEFAULT_VARIANT_SETTINGS={
  rankWeight:1.0
};

const INTERPOLATION_FIELDS=[
  'filename'
];

@Injectable({
  providedIn: 'root'
})
export class LayersService {
  layerConfig$: Observable<LayerDescriptor[]>;

  constructor(private http: CacheService,
              private featureData: FeatureDataService) {
    const url = `${environment.layerConfig}?_=${(new Date()).getTime()}`;
    this.layerConfig$ = this.http.get(url).pipe(
      map((origLayers:LayerDescriptor[])=>{
        const constants:any = origLayers.filter(l=>l.type==='constants')[0];
        let layers:LayerDescriptor[] = origLayers.filter(l=>l.type!=='constants');
        layers = layers.map(l=>{
          if(l.variants?.length){
            l.variants = l.variants.map(v=>{
              return {...DEFAULT_VARIANT_SETTINGS,...v};
            });
          }
          return {...DEFAULT_LAYER_SETTINGS,...l};
        });

        if(constants){
          layers.forEach(l=>{
            this.substituteConstants(l, constants);
          });
        }

        layers.forEach(l=>{
          if(l.source==='terrakio'){
            const server = l.server || environment.terrakio;
            l.url = environment.terrakioWMS.includes('://')? environment.terrakioWMS : `${server}/${environment.terrakioWMS}`;
            l.metadata = `${server}/datasets/${l.collection||l.variants[0].collection}`;
            l.polygonDrill = `${server}/${environment.terrakioWCS}`;
            if(environment.terrakioKey){
              l.polygonDrillHeaders = {...l.polygonDrillHeaders,'x-api-key':environment.terrakioKey};
            }
          }
        });
        return layers;
      }),
      map((rawLayers:LayerDescriptor[])=>{
        return forkJoin(rawLayers.map(l=>{
          // TODO: Deprecated. Not used at all????
          if(l.timePeriod){
            l.timePeriod = DateRange.fromJSON(l.timePeriod);
          } else if(l.time) {
            // infer time period
            return this.featureData.getTimes(l).pipe(
              map(times=>{
                if(times){
                  l.timePeriod = new DateRange();
                  l.timePeriod.start = times[0];
                  l.timePeriod.end = times[times.length-1];
                }
                return l;
              })
            );
          }
          return of(l);
        }));
      }),
      switchAll(),
      publishReplay(),
      refCount()) as Observable<LayerDescriptor[]>;
  }

  private substituteConstants(l: LayerDescriptor, constants: any, mainConfig?:any) {
    mainConfig = mainConfig || constants;
    mainConfig = {...mainConfig,...l};
    Object.keys(l).forEach(k => {
      const val = (l as any)[k];
      if (typeof (val) === 'string'){
        if(val.startsWith('$') && val.endsWith('$')) {
          (l as any)[k] = constants[val.slice(1, -1)]
        }
        (l as any)[k] = InterpolationService.interpolate((l as any)[k],mainConfig);
      } else if (val && (typeof (val) === 'object')) {
        this.substituteConstants(val, constants,mainConfig);
      }
    });
  }

  matchingLayers(params?: any): Observable<LayerDescriptor[]>{
    if(!params){
      return this.layerConfig$;
    }

    const keys = Object.keys(params);

    if(!keys.length){
      return this.layerConfig$;
    }

    return this.layerConfig$.pipe(
      map(allLayers=>{
        return allLayers.filter(l=>{
          return keys.every(k=>(l as any)[k]===params[k]);
        });
      })
    );
  }

  constrainDate(d: UTCDate, lyr: LayerDescriptor): UTCDate {
    // TODO: Deprecated? Unused?
    if(!lyr.timePeriod){
      return d;
    }
    if(lyr.timePeriod.contains(d)){
      return d;
    }
    if(d.getTime()<lyr.timePeriod.start.getTime()){
      return lyr.timePeriod.start;
    }
    return lyr.timePeriod.end;
  }

  availableDates(lyr:LayerDescriptor): Observable<UTCDate[]>{
    if(lyr.type==='grid'){
      return this.availableDatesGridLayer(lyr);
    }

    return this.availableDatesFeatureLayer(lyr);
  }

  availableDatesFeatureLayer(lyr: LayerDescriptor): Observable<UTCDate[]> {
    return this.featureData.getTimes(lyr).pipe(
      map(times=>{
        return times.map(t=>new Date(Date.UTC(t.getUTCFullYear(),t.getUTCMonth(),1))).filter((v,i,a)=>a.indexOf(v)===i);
      })
    );
  }

  availableDatesGridLayer(lyr: LayerDescriptor): Observable<UTCDate[]> {
    if(lyr.metadata){
      return this.getLayerMetadata(lyr).pipe(
        map(metadata=>{
          if(metadata.dates_regular){
            metadata.dates_iso8601 = create_date_range(metadata.dates_regular);
          }
          let dates = R.uniq((metadata.dates_iso8601||[]) as string[]).map(d=>new Date(d));
          if(lyr.timePeriod){
            dates = dates.filter(d=>lyr.timePeriod.contains(d));
          }
          return dates;
        })
      );
    }


    const interval = lyr.timePeriod.interval || {days:1};
    let d = lyr.timePeriod.start;
    const result:UTCDate[] = [];
    while(d<=lyr.timePeriod.end){
      result.push(d);
      d = new Date(d.getTime());
      if(interval.years){
        d.setUTCFullYear(d.getUTCFullYear()+interval.years);
      }
      if(interval.months){
        d.setUTCMonth(d.getUTCMonth()+interval.months);
      }
      if(interval.days){
        d.setUTCDate(d.getUTCDate()+interval.days);
      }
    }

    return of(result);
  }

  getLayerMetadata(flatLayer:LayerDescriptor): Observable<any>{
    if(!flatLayer?.metadata){
      return of(null);
    }

    const url = InterpolationService.interpolate(
      flatLayer.metadata,flatLayer) + '?api-key=' + environment.terrakioKey;

    return this.http.get(url).pipe(
      tap(ensureDatesAreFirstOfMonth)
    );
  }
}


function ensureDatesAreFirstOfMonth(metadata:any){
  // HACK to deal with data that is sub-monthly (eg 4 day MODIS)
  if(!metadata?.dates_iso8601){
    return;
  }

  metadata.dates_iso8601 = metadata.dates_iso8601.map((d:string)=>{
    return d.substring(0,8) + '01' + d.substring(10);
  });
}

function create_date_range(dates_regular:{start:string,end:string,freq:string}):string[]{
  if(dates_regular?.freq!=='1month'){
    throw new Error('Only monthly frequency supported');
  }
  const result:string[] = [];
  let current = new Date(dates_regular.start);
  const end = new Date(dates_regular.end);
  while(current<=end){
    result.push(current.toISOString());
    current = new Date(current.getTime());
    current.setUTCMonth(current.getUTCMonth()+1);
  }
  return result;
}